import { useMemo } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query'; //-tech-debt-react-query;
import { useSelector } from 'react-redux';
import { CacheKey } from '../../types/cache-keys';
import { RootState } from '../../support/store';

/**
 * Access to the published documentation bundle in `public/docs`.
 *
 * The bundle is static and versioned with the app build, so it is fetched directly
 * rather than through the API query helpers. Swapping to a backend-served bundle later
 * means changing only the two fetches below.
 */

export const DOCS_BASE = '/docs';

export type DocAudience = 'end-user' | 'admin';

export interface DocHeading {
  depth: number;
  text: string;
  anchor: string;
}

export interface DocEntry {
  id: string;
  title: string;
  route: string;
  permission: string;
  path: string;
  /** ISO date the page was documented, shown to the reader. */
  capturedAt: string;
  /** Best-known app version at capture time. Informational only. */
  appVersion?: string;
  order: number;
  tags: string[];
  headings: DocHeading[];
}

export interface DocsManifest {
  generatedAt: string;
  appVersion: string;
  trees: Record<DocAudience, DocEntry[]>;
}

export const AUDIENCE_LABELS: Record<DocAudience, string> = {
  'end-user': 'Using Security Hub',
  admin: 'Administration',
};

const EMPTY_TREES: Record<DocAudience, DocEntry[]> = { 'end-user': [], admin: [] };

// Docs change only on deploy, so there is no value in refetching during a session.
const DOCS_QUERY_OPTIONS = {
  staleTime: Infinity,
  gcTime: 1000 * 60 * 60,
  retry: false,
  refetchOnWindowFocus: false,
};

const fetchJson = async (url: string) => {
  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Documentation unavailable (${response.status})`);
  return response.json();
};

const fetchText = async (url: string) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Page unavailable (${response.status})`);
  return response.text();
};

export const useDocsManifest = (enabled = true): UseQueryResult<DocsManifest> =>
  useQuery<DocsManifest>({
    queryKey: [String(CacheKey.Documentation)],
    queryFn: () => fetchJson(`${DOCS_BASE}/manifest.json`),
    ...{ ...DOCS_QUERY_OPTIONS, enabled },
  });

export const useDocContent = (path?: string): UseQueryResult<string> =>
  useQuery<string>({
    queryKey: [String(CacheKey.DocumentationPage), String(path)],
    queryFn: () => fetchText(`${DOCS_BASE}/${path}`),
    ...{ ...DOCS_QUERY_OPTIONS, enabled: !!path },
  });

/**
 * The manifest filtered to what this user may see, using the same rule as
 * `canAccessPage` in App.tsx: `admin.full` bypasses, otherwise the entry's permission
 * must be held. Admin documentation is therefore invisible to non-admins.
 */
export const useVisibleDocs = (enabled = true) => {
  const authz = useSelector((state: RootState) => state.user?.authz);
  const query = useDocsManifest(enabled);

  const permSet = useMemo(
    () => new Set<string>(authz?.uiPermissions || []),
    [authz?.uiPermissions]
  );

  const trees = useMemo(() => {
    const source = query.data?.trees;
    if (!source) return EMPTY_TREES;
    if (permSet.has('admin.full')) return source;

    const visible = { ...EMPTY_TREES };
    (Object.keys(source) as DocAudience[]).forEach((audience) => {
      visible[audience] = (source[audience] || []).filter((entry) =>
        permSet.has(entry.permission)
      );
    });
    return visible;
  }, [query.data, permSet]);

  const allEntries = useMemo(
    () => (Object.keys(trees) as DocAudience[]).flatMap((a) => trees[a]),
    [trees]
  );

  return { ...query, trees, allEntries, manifest: query.data };
};
